import { Box } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { TimeField } from '@mui/x-date-pickers/TimeField'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { MutableRefObject } from 'react'
import { Controller } from 'react-hook-form'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import SelectItemWithSearchPopup, {
  SelectableItem
} from 'components/popups/SelectItemWithSearchPopup'
import dateToTimeString from 'components/calendar/components'
import { minutesToHumanReadable } from 'components/popups/eventInfo/AddReminderPopup'
import { theme } from 'theme'
import { isAMPM } from 'utils/date'
import { isCypress } from 'utils/cypress'
import i18n from 'i18n'

export interface TimePickerBoxProps {
  allDay: boolean

  dayjsTime: Dayjs
  timeFormat?: (timestamp: number) => string
  shouldShowTimeDropdown?: boolean
  showTimeMinutesPopup: boolean
  setShowTimeMinutesPopup: (show: boolean) => void
  buildMinutes: () => SelectableItem[]
  onAcceptDate: (date: Dayjs | null, time?: boolean) => void
  setTime: (date: Dayjs | null) => void
  timeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>
  tz: string
  isStartPicker: boolean
  withUnderline?: boolean
  shouldScrollToTime?: boolean
}

export interface TimeRowProps {
  item: SelectableItem
  setShowTimeMinutesPopup: (show: boolean) => void
  onAcceptDate: (date: Dayjs | null, time?: boolean) => void
}

dayjs.extend(utc)
dayjs.extend(timezone)

export function buildTimeRow(props: TimeRowProps) {
  const { item, setShowTimeMinutesPopup, onAcceptDate } = { ...props }
  return (
    <Box
      key={item.title}
      id={item.title}
      onClick={(e) => {
        e.stopPropagation()
        setShowTimeMinutesPopup(false)
        onAcceptDate(item.value, true)
      }}
      width={'fit-content'}
      display={'flex'}
      alignContent={'center'}
      flexDirection={'row'}
      color={'black'}
      sx={{
        cursor: 'pointer',
        fontSize: '14px',

        '&:hover': {
          backgroundColor: theme.palette.background.paper
        }
      }}
      gap={4}
    >
      <div
        style={{
          padding: '4px',
          minWidth: isAMPM() ? '70px' : '45px'
        }}
      >
        {item.title}
      </div>
      {item.subTitle && (
        <div
          style={{
            color: theme.palette.text.secondary,
            textAlign: 'center',
            padding: '4px'
          }}
        >
          {item.subTitle}
        </div>
      )}
    </Box>
  )
}

export function buildTimeOptions(
  baseTime: Dayjs,
  duration?: number,
  _subtract: boolean = false,
  wholeDay: boolean = false,
  subTitle: boolean = false,
  referenceTime?: Dayjs
): SelectableItem[] {
  let minutes: SelectableItem[] = []

  if (referenceTime !== undefined) {
    for (let i = 1; i < 5; i++) {
      const minuteOffset = i * 15
      let time: Dayjs

      time = referenceTime.add(minuteOffset, 'minute')

      const selectableTime: SelectableItem = {
        title: dateToTimeString(time.toDate(), true),
        subTitle: !subTitle ? undefined : minutesToHumanReadable(minuteOffset),
        value: time
      }
      minutes.push(selectableTime)
    }
  }

  for (let i = wholeDay ? 0 : 1; i < (wholeDay ? 48 : 7); i++) {
    const minuteOffset = (i + 1) * 30
    let time: Dayjs

    time =
      duration !== undefined
        ? baseTime.add(0 - duration, 'milliseconds').add(minuteOffset, 'minute')
        : baseTime.add(minuteOffset, 'minute')

    const selectableTime: SelectableItem = {
      title: dateToTimeString(time.toDate(), true),
      subTitle: !subTitle
        ? undefined
        : minutesToHumanReadable(
            time.diff(referenceTime ?? baseTime, 'minute') * -1
          ),
      value: time
    }
    minutes.push(selectableTime)
  }
  return minutes
}

export function TimePickerBox({
  allDay,

  dayjsTime,
  timeFormat,
  shouldShowTimeDropdown,
  showTimeMinutesPopup,
  setShowTimeMinutesPopup,
  buildMinutes,
  onAcceptDate,
  setTime,
  timeoutRef,
  tz,
  isStartPicker,
  withUnderline,
  shouldScrollToTime
}: TimePickerBoxProps) {
  if (allDay) return <></>

  function togglePopupVisibility(b: boolean) {
    setShowTimeMinutesPopup(b)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <SelectItemWithSearchPopup
        data={buildMinutes()}
        width="fit-content"
        maxHeight="110px"
        placement="bottom"
        scrollToKey={
          shouldScrollToTime
            ? dateToTimeString(dayjsTime.startOf('hour').toDate(), true)
            : undefined
        }
        snapScroll
        open={showTimeMinutesPopup}
        selectedItemId={undefined}
        rowBuilder={(item) => {
          return buildTimeRow({
            item,
            setShowTimeMinutesPopup: togglePopupVisibility,
            onAcceptDate
          })
        }}
      >
        <PopupSectionRow
          actionBg
          gap="0rem"
          flexGrowForRow={0}
          actionPadding="4px 0px"
          action={() => {
            togglePopupVisibility(true)
          }}
          title={
            <TimeField
              id={isStartPicker ? 'start-time-picker' : 'end-time-picker'}
              ampm={isCypress() ? false : isAMPM()}
              value={dayjsTime}
              variant="standard"
              inputProps={{
                style: {
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: 0,

                  textAlign: 'center',
                  height: '100%',
                  maxWidth: isAMPM() ? '70px' : '45px',

                  borderBottom: withUnderline ? '1px solid #E4E4E4' : 'none'
                }
              }}
              onBlur={() => {}}
              InputProps={{ disableUnderline: true }}
              onChange={(newValue) => {
                togglePopupVisibility(false)

                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current)
                }

                timeoutRef.current = setTimeout(() => {
                  setTime(newValue)
                  onAcceptDate(newValue, true)
                }, 500)
              }}
            />
          }
        />
      </SelectItemWithSearchPopup>
    </LocalizationProvider>
  )
}
